import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../hooks';
import { AppDialogTitle } from '../../layout/appDialogTitle';
import {
  Employee,
  EmployeeRole,
  ROLES,
  ROLES_ID,
  RolesNamesBE,
} from '../../models/employee';
import {
  addEmployeeRole,
  getUserPermissionGroups,
} from '../../services/api/employeeServices';
import { orderRoles } from '../../services/roleNamesService';
import {
  selectProfile,
  updateCurrentUserRoles,
} from '../../store/profileSlice';
import { AppButton } from '../app/button/appButton';
import { AppConfirmationSuccessful } from '../app/confirmation/appConfirmationSuccessful';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';

interface Prop {
  onClose(): void;
  isOpen: boolean;
  employee: Employee | null;
  updateEmployeePermissionGroups: (
    employeeId: string,
    permissionGroups: EmployeeRole[]
  ) => void;
}

export default function EmployeeAddRoleDialog({
  onClose,
  isOpen,
  employee,
  updateEmployeePermissionGroups,
}: Prop): JSX.Element {
  const [roles, setRoles] = useState<EmployeeRole[]>([]);
  const [isOpenConfirmation, setIsOpenConfirmation] = useState<boolean>(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState<boolean>(false);

  const currentUser = useSelector(selectProfile);

  const dispatch = useAppDispatch();

  const methods = useForm();

  useEffect(() => {
    getUserPermissionGroups().then((response) => {
      setRoles(orderRoles(response));
    });
  }, []);

  useEffect(() => {
    methods.reset();
  }, [employee, methods]);

  const handleClose = useCallback(() => {
    setIsOpenConfirmation(false);
  }, []);

  const formatEmployeeRoles = useCallback((rolesIds: string[]) => {
    return rolesIds
      .map((roleId) => {
        const roleKey = Object.keys(ROLES_ID).find(
          (key) => ROLES_ID[key as keyof typeof ROLES_ID] === roleId
        );

        if (!roleKey) {
          return null;
        }

        return {
          id: roleId,
          name: RolesNamesBE[roleKey as keyof typeof RolesNamesBE],
        };
      })
      .filter((role): role is EmployeeRole => role !== null);
  }, []);

  const onSubmit = useCallback(
    (data) => {
      setIsLoadingRequest(true);
      const rolesAsArray = Object.keys(data).filter((key) => data[key]);

      if (employee)
        addEmployeeRole(employee.id, rolesAsArray)
          .then((result) => {
            onClose();
            setIsOpenConfirmation(true);
            if (employee.id === currentUser?.id) {
              dispatch(updateCurrentUserRoles(result.permissionGroups));
            }
          })
          .then(() => {
            updateEmployeePermissionGroups(
              employee.id,
              formatEmployeeRoles(rolesAsArray)
            );

            setTimeout(() => {
              setIsOpenConfirmation(false);
            }, 1000);
          })
          .finally(() => setIsLoadingRequest(false));
    },
    [
      employee,
      onClose,
      currentUser?.id,
      dispatch,
      updateEmployeePermissionGroups,
      formatEmployeeRoles,
    ]
  );

  const isChecked = useCallback(
    (role) =>
      employee?.permissionGroups.some((userRole) => userRole.id === role.id),
    [employee]
  );

  const handleChangeFields = (
    role: EmployeeRole,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isFieldChecked = event.target.checked;

    if (role.id === ROLES_ID.SALES_MANAGER && isFieldChecked) {
      methods.setValue(ROLES_ID.PROJECT_MANAGER, isFieldChecked);
    }

    if (role.id === ROLES_ID.PROJECT_MANAGER && !isFieldChecked) {
      methods.setValue(ROLES_ID.SALES_MANAGER, false);
    }
  };

  return (
    <div>
      <Dialog open={isOpen} sx={{ borderRadius: '5px' }}>
        <AppDialogTitle
          title="Manage Roles"
          handleClose={onClose}
          sx={{ minWidth: '340px' }}
        />

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {roles.map((role) => (
                  <AppControlledCheckbox
                    key={role.id}
                    name={role.id}
                    label={ROLES[role.name]}
                    defaultValue={isChecked(role)}
                    isDisabled={
                      role.name === RolesNamesBE.EMPLOYEE ||
                      role.name === RolesNamesBE.TEAM_LEAD
                    }
                    onChangeFn={(event) => handleChangeFields(role, event)}
                  />
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <AppButton label="Cancel" onClick={onClose} />
              <AppButton
                label="Save"
                isPrimary
                buttonType="submit"
                isLoading={isLoadingRequest}
              />
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <AppConfirmationSuccessful
        isOpen={isOpenConfirmation}
        message="Roles updated"
        onClose={handleClose}
      />
    </div>
  );
}
